import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IFormValues,
  IUcApiRequest,
  IUcApiResponse,
} from "../../shared/interfaces";
import { useApi } from "../../hooks/useApi";
import { UcService } from "../../service/UcService";
import { ToastMessage } from "primereact/toast";
import { getOnlyNumbers } from "../../shared/utils/getOnlyNumbers";

export function useUcSearchScreen(executeRecaptcha: ((action?: string) => Promise<string>) | null) {
  const navigate = useNavigate();
  const { call } = useApi();

  const [oldUc, setOldUc] = useState<number | null>(null);
  const [newUc, setNewUc] = useState<string>("");
  const [showInvoiceModal, setShowInvoiceModal] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  useEffect(() => {
    setButtonDisabled(!oldUc && !newUc);
  }, [oldUc, newUc]);

  const captchaValidation = async (
    toast: (options: ToastMessage | ToastMessage[]) => void
  ) => {
    if (!executeRecaptcha) {
      toast({
        severity: "error",
        summary: "Erro no reCAPTCHA",
        detail: "O reCAPTCHA não está disponível no momento. Tente novamente mais tarde.",
      });
      return "invalid";
    }

    try {
      // Executar o reCAPTCHA v3 com uma ação específica
      const token = await executeRecaptcha('submit_form');

      if (!token) {
        toast({
          severity: "error",
          summary: "Erro no reCAPTCHA",
          detail: "Não foi possível validar o reCAPTCHA. Tente novamente.",
        });
        return "invalid";
      }

      return token;
    } catch (error) {
      console.error("Erro ao executar o reCAPTCHA:", error);
      toast({
        severity: "error",
        summary: "Erro no reCAPTCHA",
        detail: "Ocorreu um erro ao validar o reCAPTCHA. Tente novamente.",
      });
      return "invalid";
    }
  };

  const doesNeedConfirmation = (response: IUcApiResponse) => {
    if (response.consultaUcResponse.indicCoexistente) {
      return true;
    }
    return false;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    call({
      loading: true,
      try: async (toast) => {
        const captchaToken = await captchaValidation(toast);
        if (captchaToken === "invalid") return;

        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);

        const formValues: IFormValues = {};
        formData.forEach((value, key) => {
          formValues[key] = value.toString();
        });

        const request: IUcApiRequest = {
          consultaUcRequest: {
            ucAntiga: formValues.oldUc,
            ucANEEL: getOnlyNumbers(formValues.newUc),
            numeroPredial: null,
            primeiroNomeDaMae: "",
          },
        };

        const response = await UcService.searchUc(request, captchaToken);

        console.log("UC Search Response:", response);

        const needsConfirmation = doesNeedConfirmation(response);

        if (needsConfirmation) {
          navigate("/confirmation", { state: { request, response } });
        } else {
          navigate("/result", { state: { response } });
        }
      },
    });
  };

  const handleOldUcChange = (e: { value: number | null }) => {    
    if(e.value === null) {
      setButtonDisabled(true);
    } else {
      setButtonDisabled(false);
    }

    setOldUc(e.value);
    setNewUc("");
  };

  const handleNewUcChange = (e: {
    target: {
      value: string;
      name?: string;
    };
  }) => {
    setNewUc(e.target.value);
    setOldUc(null);
  };

  const handleClean = () => {
    setOldUc(null);
    setNewUc("");
  };

  return {
    handleSubmit,
    oldUc,
    setOldUc,
    newUc,
    setNewUc,
    handleOldUcChange,
    handleNewUcChange,
    handleClean,
    showInvoiceModal,
    setShowInvoiceModal,
    buttonDisabled,
  };
}
